import {
  BankOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { Button, DatePicker, Form, InputNumber, message, Modal, Select, Tooltip } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import SummaryLayout from '../../components/layout/SummaryLayout';
import MonthSelector from '../../components/ui/MonthSelector';
import { getData, postData } from '../../config/api-connect';
import { useCityName } from '../../hooks/useCityName';
import { useMonthFilter } from '../../hooks/useMonthFilter';
import { getColumnFilters, getColumnSearchProps } from '../../utils/filters';
import { formatCurrency, renderWithEllipsis, uniqueValues } from '../../utils/helper';

const { Option } = Select;

function FranchiseInvoices() {
  const { selectedMonth, setSelectedMonth } = useMonthFilter();
  const { cityName, setCityName } = useCityName();
  const [form] = Form.useForm();

  const cityOptions = [
    'Bangalore',
    'Kerala',
    'Hyderabad',
    'Qatar',
    'UAE',
    'Kuwait',
    'Poland',
    'France',
    'Germany',
    'Thailand',
  ];

  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [allVenues, setAllVenues] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);

  const isEditMode = !!editingInvoice;

  useEffect(() => {
    getData('/finance/get_cities_and_venues/?has_invoice_code=true')
      .then((response) => {
        setAllVenues(response.venues || []);
        setInitialized(true);
      })
      .catch((error) => console.error('Initialization Error:', error.message));
  }, []);

  const getFranchiseInvoices = useCallback(() => {
    setLoading(true);

    const month = selectedMonth.format('MM');
    const year = selectedMonth.format('YYYY');

    const cityQuery = cityName ? `&city_name=${cityName}` : '';

    getData(`/finance/get_gst_invoices/?month=${month}&year=${year}&type=Franchise${cityQuery}`)
      .then((response) => {
        setInvoices(response.result || []);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        console.error('Error fetching invoices:', error.message);
      });
  }, [cityName, selectedMonth]);

  useEffect(() => {
    if (!initialized) return;
    getFranchiseInvoices();
  }, [initialized, cityName, selectedMonth, getFranchiseInvoices]);

  const handleSubmitInvoice = (values) => {
    setSubmitLoading(true);

    const month = values.invoice_month?.format('MM');
    const year = values.invoice_month?.format('YYYY');

    const api = isEditMode
      ? `/finance/edit_franchise_invoice/`
      : `/finance/create_franchise_invoice/?month=${month}&year=${year}&venue_id=${values.venue_id}&total_amount=${values.total}`;

    const payload = isEditMode
      ? {
          invoice_id: editingInvoice.id,
          total_amount: values.total,
        }
      : {};

    postData(api, payload)
      .then((response) => {
        if (response.success) {
          message.success(
            response.message ||
              `Franchise invoice ${isEditMode ? 'updated' : 'created'} successfully.`,
          );

          setIsModalOpen(false);
          setEditingInvoice(null);
          form.resetFields();
          getFranchiseInvoices();
        } else {
          message.error(response.message || 'Operation failed.');
        }
      })
      .catch((error) => {
        message.error(error.message || 'An error occurred.');
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  };

  const handleDeleteInvoice = (invoice) => {
    Modal.confirm({
      centered: true,
      width: 420,
      icon: null,

      title: (
        <div
          style={{
            fontSize: '20px',
            fontWeight: 600,
            textAlign: 'center',
            marginBottom: '8px',
          }}
        >
          Delete Invoice?
        </div>
      ),

      content: (
        <div
          style={{
            textAlign: 'center',
            color: '#6e6e73',
            fontSize: '15px',
            lineHeight: '22px',
            padding: '0 12px',
          }}
        >
          Are you sure you want to permanently delete{' '}
          <span style={{ fontWeight: 600, color: '#1d1d1f' }}>{invoice.invoice_number}</span>
          ?
          <br />
          This action cannot be undone.
        </div>
      ),

      okText: 'Delete',
      cancelText: 'Cancel',

      okButtonProps: {
        danger: true,
        style: {
          borderRadius: '10px',
          fontWeight: 500,
          height: '38px',
          minWidth: '90px',
        },
      },

      cancelButtonProps: {
        style: {
          borderRadius: '10px',
          height: '38px',
          minWidth: '90px',
        },
      },

      onOk: () => {
        postData('/finance/delete_franchise_invoice/', {
          invoice_id: invoice.id,
        })
          .then((response) => {
            if (response.success) {
              message.success(response.message || 'Invoice deleted successfully.');
              getFranchiseInvoices();
            } else {
              message.error(response.message || 'Failed to delete invoice.');
            }
          })
          .catch((error) => {
            message.error(error.message || 'An error occurred.');
          });
      },
    });
  };

  const handleEditInvoice = (invoice) => {
    setEditingInvoice(invoice);

    form.setFieldsValue({
      venue_id: invoice.venue.id,
      total: Number(invoice.total),
    });

    setIsModalOpen(true);
  };

  const columns = [
    {
      title: 'Invoice Number',
      dataIndex: 'invoice_number',
      align: 'center',
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      ...getColumnSearchProps('invoice_number'),
      onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
    },
    {
      title: 'Venue',
      dataIndex: 'venue',
      align: 'left',
      width: 250,
      render: (venue) => renderWithEllipsis(venue?.name, { width: 200, lines: 2 }),
      ...getColumnFilters('venue.name', uniqueValues(invoices, 'venue', 'name')),
    },
    {
      title: (
        <div style={{ lineHeight: 1.2, textAlign: 'center' }}>
          <div>Net Income</div>
          <div style={{ fontSize: 12, fontWeight: 400 }}>(incl. GST)</div>
        </div>
      ),
      dataIndex: 'total',
      align: 'right',
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      sorter: (a, b) => (Number(a.total) || 0) - (Number(b.total) || 0),
      render: (val) => (val == null ? '—' : <strong>{formatCurrency(val)}</strong>),
    },
    {
      title: (
        <div style={{ lineHeight: 1.2, textAlign: 'center' }}>
          <div>Net Income</div>
          <div style={{ fontSize: 12, fontWeight: 400 }}>(excl. GST)</div>
        </div>
      ),
      dataIndex: 'taxable_value',
      align: 'right',
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      sorter: (a, b) => (Number(a.taxable_value) || 0) - (Number(b.taxable_value) || 0),
      render: (val) => (val == null ? '—' : formatCurrency(val)),
    },
    {
      title: 'CGST',
      dataIndex: 'cgst',
      align: 'right',
      render: (val) => (val == null ? '—' : formatCurrency(val)),
    },
    {
      title: 'SGST',
      dataIndex: 'sgst',
      align: 'right',
      render: (val) => (val == null ? '—' : formatCurrency(val)),
    },
    {
      title: 'Total Tax',
      key: 'total_gst',
      align: 'right',
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (_, record) => {
        const totalGst = (Number(record.cgst) || 0) + (Number(record.sgst) || 0);
        return totalGst === 0 && record.cgst == null ? '—' : formatCurrency(totalGst);
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      fixed: 'right',
      width: 140,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '14px', justifyContent: 'center' }}>
          <Tooltip title="View Invoice PDF">
            <EyeOutlined
              style={{
                fontSize: '18px',
                color: record.pdf ? '#1890ff' : '#ccc',
                cursor: record.pdf ? 'pointer' : 'not-allowed',
              }}
              onClick={(e) => {
                if (!record.pdf) return;
                e.stopPropagation();
                window.open(record.pdf, '_blank', 'noopener,noreferrer');
              }}
            />
          </Tooltip>

          <Tooltip title="Edit Invoice">
            <EditOutlined
              style={{
                color: '#1890ff',
                cursor: 'pointer',
                fontSize: '16px',
              }}
              onClick={() => handleEditInvoice(record)}
            />
          </Tooltip>

          <Tooltip title="Delete Invoice">
            <DeleteOutlined
              style={{
                color: '#ff4d4f',
                cursor: 'pointer',
                fontSize: '16px',
              }}
              onClick={() => handleDeleteInvoice(record)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const pageTitle = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <span>GST Invoices - Franchise</span>
    </div>
  );

  const chooseCityName = (value) => {
    setCityName(value);
  };

  const renderRightSideActions = () => (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      <Select
        showSearch
        placeholder="Select City"
        style={{ width: 170 }}
        value={cityName}
        onChange={(value) => chooseCityName(value)}
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.toLowerCase().includes(input.toLowerCase())
        }
      >
        <Option value="">All Cities</Option>
        {cityOptions.map((city) => (
          <Option key={city} value={city}>
            {city}
          </Option>
        ))}
      </Select>

      <MonthSelector value={selectedMonth} onChange={setSelectedMonth} />

      <Button
        type="primary"
        style={{ display: 'flex', alignItems: 'center' }}
        icon={<PlusOutlined />}
        onClick={() => {
          form.setFieldsValue({
            invoice_month: selectedMonth,
          });
          setIsModalOpen(true);
        }}
      >
        Add Invoice
      </Button>
    </div>
  );

  const totalNetIncl = invoices.reduce((acc, curr) => acc + (Number(curr.total) || 0), 0);
  const totalNetExcl = invoices.reduce((acc, curr) => acc + (Number(curr.taxable_value) || 0), 0);
  const totalGst = invoices.reduce(
    (acc, curr) => acc + (Number(curr.cgst) || 0) + (Number(curr.sgst) || 0),
    0,
  );

  const statsConfig = [
    {
      key: 's_invoices',
      title: 'Total Invoices',
      icon: <FileTextOutlined style={{ fontSize: 32, color: '#1890ff' }} />,
    },
    {
      key: 's_taxable',
      title: 'Net Income (excl. GST)',
      icon: <DatabaseOutlined style={{ fontSize: 32, color: '#1565c0' }} />,
    },
    {
      key: 's_gst',
      title: 'Total GST Collected',
      icon: <SafetyCertificateOutlined style={{ fontSize: 32, color: '#faad14' }} />,
    },
    {
      key: 's_total',
      title: 'Net Income (incl. GST)',
      icon: <BankOutlined style={{ fontSize: 32, color: '#52c41a' }} />,
    },
  ];

  const totals = {
    s_invoices: invoices.length,
    s_taxable: `₹ ${totalNetExcl.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`,
    s_gst: `₹ ${totalGst.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`,
    s_total: `₹ ${totalNetIncl.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`,
  };

  return (
    <>
      <SummaryLayout
        title={pageTitle}
        headerContent={renderRightSideActions()}
        statsConfig={statsConfig}
        totals={totals}
        statsLoading={loading}
        tableProps={{
          rowKey: 'id',
          columns,
          data: invoices,
          loading: loading,
          pagination: { pageSize: 20, showSizeChanger: false },
        }}
      />

      <Modal
        title={isEditMode ? 'Edit Franchise Invoice' : 'Add New Franchise Invoice'}
        open={isModalOpen}
        onOk={() => form.submit()}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingInvoice(null);
          form.resetFields();
        }}
        okText={isEditMode ? 'Update' : 'Add'}
        cancelText="Cancel"
        confirmLoading={submitLoading}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmitInvoice}>
          <Form.Item
            name="venue_id"
            label={
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>Venue</span>

                <Tooltip title="Only venues with an invoice code are listed here">
                  <InfoCircleOutlined
                    style={{
                      color: '#1677ff',
                      cursor: 'pointer',
                      fontSize: 14,
                    }}
                  />
                </Tooltip>
              </div>
            }
            rules={[{ required: true, message: 'Please select a venue' }]}
          >
            <Select
              disabled={isEditMode}
              showSearch
              placeholder="Select a venue"
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {allVenues.map((venue) => (
                <Option key={venue.id} value={venue.id}>
                  {venue.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          {!isEditMode && (
            <Form.Item
              name="invoice_month"
              label="Invoice Month"
              rules={[{ required: true, message: 'Please select a month' }]}
            >
              <DatePicker picker="month" style={{ width: '100%' }} format="MMM YYYY" />
            </Form.Item>
          )}
          <Form.Item
            name="total"
            label="Total (Net Income incl. GST)"
            rules={[{ required: true, message: 'Please enter the total amount' }]}
          >
            <InputNumber style={{ width: '100%' }} placeholder="Enter total amount" min={0} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default FranchiseInvoices;
